import React, { useState, useEffect } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PasswordModal from './PasswordModal';
import EditStudentModal from './EditStudentModal';
import '../Dashboard.css';
import { refreshToken, isTokenExpiredError } from '../../Utils/authUtils';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Pagination, 
  Card, 
  Tooltip, 
  message,
  Modal,
  Upload
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  UserOutlined, 
  UploadOutlined,
  StarOutlined, 
  FilePdfOutlined, 
  UserAddOutlined,
} from '@ant-design/icons';

function EditDeleteStudent({ onCreateNewStudent }) {
    const { t } = useTranslation();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [studentIdToDelete, setStudentIdToDelete] = useState(null);
    const [studentIdToEdit, setStudentIdToEdit] = useState(null);
    const [passwordError, setPasswordError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [nameSearch, setNameSearch] = useState('');
    const [phoneSearch, setPhoneSearch] = useState('');
    const [schoolIdSearch, setSchoolIdSearch] = useState('');
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Intl.DateTimeFormat(t('locale'), options).format(date).toUpperCase();
    };

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const token = localStorage.getItem('token');

                const queryParams = new URLSearchParams({
                    page: currentPage,
                    name: nameSearch,
                    phoneNumber: phoneSearch,
                    schoolId: schoolIdSearch
                });

                const response = await axios.get(`${API_URL}/students?${queryParams.toString()}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const sortedStudents = response.data.students.sort((a, b) => new Date(b.createdat) - new Date(a.createdat));
                setStudents(sortedStudents);
                setTotalPages(response.data.totalPages);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching students:', error);
                if (isTokenExpiredError(error)) {
                    refreshToken().then(() => {
                        fetchStudents(currentPage);
                    }).catch(err => {
                        console.error('Token refresh failed:', err);
                        setError(t('sessionExpired'));
                    });
                } else {
                    setError(t('fetchStudentsError'));
                    setLoading(false);
                }
            }
        };

        fetchStudents();
    }, [currentPage, nameSearch, phoneSearch, schoolIdSearch, API_URL, t]);
    

    const handleEdit = (studentId) => {
        setStudentIdToEdit(studentId);
        setIsEditModalOpen(true);
    };

    const handleDelete = (studentId) => {
        setStudentIdToDelete(studentId);
        setPasswordError('');
        setIsPasswordModalOpen(true);
    };

    const confirmDelete = async (password) => {
        setIsPasswordModalOpen(false);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError(t('noTokenFound'));
                return;
            }
            await axios.delete(`${API_URL}/students/${studentIdToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                data: { password }
            });
            setStudents(prev => prev.filter(student => student.userid !== studentIdToDelete));
            message.success(t('studentDeleted'));
        } catch (error) {
            console.error('Error deleting student:', error);
            if (error.response && error.response.status === 401) {
                setPasswordError(t('invalidPassword'));
                setIsPasswordModalOpen(true); 
            } else {
                setError(t('deleteStudentError'));
                message.error(t('deleteStudentError'));
            }
        }
    };

  const saveStudent = async (updatedStudent) => {
    try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/students/${updatedStudent.userid}`, updatedStudent, {
            headers: { Authorization: `Bearer ${token}` }
        });
        
        // Refresh the student list to get the updated data including national_id_pdf
        const queryParams = new URLSearchParams({
            page: currentPage,
            name: nameSearch,
            phoneNumber: phoneSearch,
            schoolId: schoolIdSearch
        });

        const response = await axios.get(`${API_URL}/students?${queryParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        setStudents(response.data.students);
        setIsEditModalOpen(false);
        message.success(t('studentUpdated'));
    } catch (error) {
        console.error('Error updating student:', error);
        setError(t('updateStudentError'));
        message.error(t('updateStudentError'));
    }
};

    const handleNationalIdUpload = async (studentId) => {
        let fileToUpload = null;
      
        Modal.confirm({
          title: t('Upload National ID'),
          content: (
            <Upload
              beforeUpload={(file) => {
                const isValidType = file.type === 'application/pdf' || 
                                    file.type === 'image/jpeg' || 
                                    file.type === 'image/jpg' || 
                                    file.type === 'image/png';
                if (!isValidType) {
                  message.error(t('onlyPdfAndImagesAllowed'));
                  return Upload.LIST_IGNORE;
                }
                
                fileToUpload = file;
                return false;
              }}
              maxCount={1}
              accept=".pdf,.jpg,.jpeg,.png"
            >
              <Button icon={<UploadOutlined />}>{t('Select File')}</Button>
            </Upload>
          ),
          onOk: async () => {
            if (!fileToUpload) {
              message.error(t('Please Select File'));
              return;
            }
      
            try {
              const token = localStorage.getItem('token');
              const formData = new FormData();
              formData.append('nationalIdFile', fileToUpload);
              formData.append('userId', studentId);
          
              await axios.post(`${API_URL}/upload/national_ids/upload`, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  'Authorization': `Bearer ${token}`
                }
              });
      
              message.success(t('National ID Uploaded Successfully'));
              
              // Refresh the student list
              const queryParams = new URLSearchParams({
                page: currentPage,
                name: nameSearch,
                phoneNumber: phoneSearch,
                schoolId: schoolIdSearch,
              });
      
              const response = await axios.get(`${API_URL}/students?${queryParams.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
      
              setStudents(response.data.students);
            } catch (error) {
              console.error('Error uploading national ID:', error);
              message.error(t('Upload Failed.'));
            }
          },
          onCancel: () => {}
        });
      };
      
    const nationalIdColumn = {
        title: t('ID'),
        dataIndex: 'national_id_pdf',
        render: (pdf, student) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {pdf ? (
                    <a 
                        href={`${API_URL}/upload/national_ids/${pdf.split('/').pop()}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                    >
                        <FilePdfOutlined style={{ color: 'red', fontSize: '20px' }} />
                    </a>
                ) : (
                    <span>-</span>
                )}
                <Tooltip title={pdf ? t('Replace ID') : t('Upload ID')}>
                    <Button 
                        size="small" 
                        icon={<UploadOutlined />}
                        onClick={() => handleNationalIdUpload(student.userid)}
                    />
                </Tooltip>
            </div>
        ),
    };

    const calculateAge = (dob) => {
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();
        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const viewStudentDashboard = (studentId) => {
        navigate(`/student-dashboard-admin/${studentId}`);
    };

    const viewStudentPerformance = (studentId) => {
        navigate(`/studentperformance/${studentId}`);
    };

    // Define columns for Ant Design Table
    const columns = [
        {
            title: '',
            key: 'dashboard',
            width: 50,
            render: (_, record) => (
                <Tooltip title={t('viewDashboard')}>
                    <Button 
                        size="medium" 
                        icon={<UserOutlined />} 
                        onClick={() => viewStudentDashboard(record.userid)}
                    />
                </Tooltip>
            )
        },
        {
            title: '',
            key: 'performance',
            width: 50,
            render: (_, record) => (
                <Tooltip title={t('viewPerformance')}>
                    <Button 
                        type="default" 
                        size="medium" 
                        icon={<StarOutlined />} 
                        onClick={() => viewStudentPerformance(record.userid)}
                    />
                </Tooltip>
            )
        },
        {
            title: t('id'),
            dataIndex: 'schoolid',
            key: 'schoolid',
        },
        {
            title: t('name'),
            key: 'name',
            render: (_, record) => `${record.firstname} ${record.lastname}`,
            ellipsis: false,
        },
        {
            title: t('nickname'),
            dataIndex: 'nickname',
            key: 'nickname',
        },
        {
            title: t('dob'),
            dataIndex: 'date_of_birth',
            key: 'date_of_birth',
            render: (date) => formatDate(date)
        },
        {
            title: t('age'),
            key: 'age',
            render: (_, record) => calculateAge(record.date_of_birth)
        },
        {
            title: t('email'),
            dataIndex: 'email',
            key: 'email',
            ellipsis: false
        },
        {
            title: t('phone'),
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: t('level'),
            dataIndex: 'currenteducationlevel',
            key: 'level',
        },
        nationalIdColumn,
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title={t('edit')}>
                        <Button 
                            icon={<EditOutlined />} 
                            size="small" 
                            onClick={() => handleEdit(record.userid)}
                        />
                    </Tooltip>
                    <Tooltip title={t('delete')}>
                        <Button 
                            danger 
                            icon={<DeleteOutlined />} 
                            size="small" 
                            onClick={() => handleDelete(record.userid)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    if (loading) return (
        <div className="loading-spinner">
            <div className="ant-spin ant-spin-lg ant-spin-spinning">
                <span className="ant-spin-dot">
                    <i className="ant-spin-dot-item"></i>
                    <i className="ant-spin-dot-item"></i>
                    <i className="ant-spin-dot-item"></i>
                    <i className="ant-spin-dot-item"></i>
                </span>
            </div>
            <div>{t('loading')}</div>
        </div>
    );
    
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div>
            <div>
                <Button 
                    type="primary" 
                    icon={<UserAddOutlined />} 
                    onClick={onCreateNewStudent}
                >
                    {t('createNewStudent')}
                </Button>

                <Card style={{ marginBottom: '16px', marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Input
                            placeholder={t('searchByName')}
                            value={nameSearch}
                            onChange={e => setNameSearch(e.target.value)}
                            style={{ maxWidth: '200px' }}
                            allowClear
                        />
                        <Input
                            placeholder={t('searchByPhone')}
                            value={phoneSearch}
                            onChange={e => setPhoneSearch(e.target.value)}
                            style={{ maxWidth: '200px' }}
                            allowClear
                        />
                        <Input
                            placeholder={t('searchBySchoolID')}
                            value={schoolIdSearch}
                            onChange={e => setSchoolIdSearch(e.target.value)}
                            style={{ maxWidth: '200px' }}
                            allowClear
                        />
                    </div>
                    
                    <Table
                        columns={columns}
                        dataSource={students}
                        rowKey="userid"
                        pagination={false}
                        size="middle"
                        scroll={{ x: 'max-content' }} 
                        style={{ overflowX: 'auto' }} 
                    />
                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'right' }}>
                        <Pagination
                            current={currentPage}
                            total={totalPages * 100}
                            pageSize={50}
                            onChange={(page) => setCurrentPage(page)}
                            showSizeChanger={false}
                        />
                    </div>
                </Card>
            </div>
            
            <PasswordModal
                isOpen={isPasswordModalOpen}
                onClose={() => setIsPasswordModalOpen(false)}
                onConfirm={confirmDelete}
                errorMessage={passwordError}
            />
            
            <EditStudentModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSave={saveStudent}
                studentId={studentIdToEdit}
            />
        </div>
    );
}

export default withRoleAccess(EditDeleteStudent, ['admin', 'superadmin']);